import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { formatLaikaSources } from './formatLaikaSources';

interface LaikaBotMessageProps {
  text: string;
  sources?: string[];
  onInternalLinkClick?: () => void;
}

const LaikaBotMessage: React.FC<LaikaBotMessageProps> = ({
  text,
  sources,
  onInternalLinkClick,
}) => {
  const { t } = useTranslation('messaging');
  const sourceLabels = formatLaikaSources(sources);
  const sourcesLabel = t('chatWidget.sourcesLabel');

  return (
    <>
      <ReactMarkdown
        components={{
          p: ({ children }) => <p className="markdown-p">{children}</p>,
          strong: ({ children }) => <strong className="markdown-strong">{children}</strong>,
          em: ({ children }) => <em className="markdown-em">{children}</em>,
          ul: ({ children }) => <ul className="markdown-ul">{children}</ul>,
          ol: ({ children }) => <ol className="markdown-ol">{children}</ol>,
          li: ({ children }) => <li className="markdown-li">{children}</li>,
          code: ({ children }) => <code className="markdown-code">{children}</code>,
          a: ({ href, children, ...props }) => {
            if (href?.startsWith('/')) {
              return (
                <Link to={href} className="markdown-link internal" onClick={onInternalLinkClick}>
                  {children}
                </Link>
              );
            }
            return (
              <a
                {...props}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="markdown-link external"
              >
                {children}
              </a>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
      {sourceLabels.length > 0 && (
        <div className="chat-message-sources" aria-label={sourcesLabel}>
          <span className="sources-label">{sourcesLabel}</span>
          {sourceLabels.map((label) => (
            <span key={label} className="source-tag">
              {label}
            </span>
          ))}
        </div>
      )}
    </>
  );
};

export default LaikaBotMessage;
